<template lang="pug">
div(:class="[$style.carouselSwiper, `carousel-${blockId}`, {[$style.carouselNarrow]: slides.length > 6}]")
  div
    swiper-container.swiper.overflow-hidden(
      ref="swiper"
      :init.prop="false"
      :loop.prop="true"
      :slides-per-view.prop="1.08"
      :space-between.prop="24"
      :breakpoints.prop="breakpointConfig"
      :pagination.prop="paginationConfig"
      :navigation.prop="navigationConfig"
    )
      swiper-slide(
        v-show="slides.length"
        v-for="(innerBlock, key) in slides"
        :key="`avcs-${key}`"
        :class="['relative flex flex-col h-auto bg-white mr-ds-lg', flushImage ? 'p-0' : 'px-ds-ml py-ds-md border border-gray-300 rounded-xl box-border']"
      )
        Block(
          :block="innerBlock.innerBlocks[0]"
          :lazy="lazy"
        )
        Block(
          :block="innerBlock.innerBlocks[1]"
          :lazy="lazy"
          :class="[flushImage ? 'flush-image-text border-l border-r border-b border-gray-300 rounded-bl-xl rounded-br-xl' : '']"
        )
    .swiper-navigation-container
      .swiper-button-prev
      .swiper-pagination
      .swiper-button-next

</template>

<script lang="ts">
import { defineComponent, onMounted, ref, nextTick } from "vue"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import type { SwiperContainer } from "swiper/element"

interface ComponentData {
  breakpointConfig: Record<number, any>
  paginationConfig: Record<string, any>
  navigationConfig: Record<string, any>
}

export default defineComponent({
  name: "CarouselSwiper",
  props: {
    block: {
      required: true,
      type: Object as () => Block
    },
    blockId: {
      type: String,
      required: true
    },
    flushImage: {
      type: Boolean
    },
    lazy: {
      type: Boolean,
      required: true
    }
  },

  setup() {
    const swiper: Ref<SwiperContainer | null> = ref(null)

    onMounted(async () => {
      if (import.meta.client) {
        nextTick(() => {
          setTimeout(() => {
            // I'm not a fan of this solution, but is the most reliable for now
            // Swiper doesn't see custom elements otherwise
            swiper.value?.initialize()
          }, 500)
        })
      }
    })

    return { swiper }
  },


  data(): ComponentData {
    return {
      breakpointConfig: {
        360: {
          slidesPerView: 1.25,
          spaceBetween: 12
        },
        430: {
          slidesPerView: 1.5,
          spaceBetween: 24
        },
        768: {
          slidesPerView: 2.25,
          spaceBetween: 30
        },
        992: {
          slidesPerView: 3.25,
          spaceBetween: 30
        },
        1200: {
          slidesPerView: 4.25,
          spaceBetween: 30
        },
        1900: {
          slidesPerView: 6,
          spaceBetween: 30
        }
      },
      paginationConfig: {
        el: `.carousel-${this.blockId} .swiper-pagination`,
        type: "bullets",
        clickable: true
      },
      navigationConfig: {
        nextEl: `.carousel-${this.blockId} .swiper-button-next`,
        prevEl: `.carousel-${this.blockId} .swiper-button-prev`
      }
    }
  },

  computed: {
    slides() {
      let blocks = this.block.innerBlocks
      if (blocks.length === 0) {
        return []
      }
      while (blocks.length <= 4) {
        blocks = [...blocks, ...blocks]
      }
      return blocks
    }
  }
})
</script>

<style lang="css" module>
:global {
  .recent-moves-section .content > div {
    /* Hacky way to target the recentMovesSection parent */
    grid-template-columns: minmax(390px, 460px) 1fr;
  }
}
.carouselSwiper {
  @apply overflow-hidden;

  :global {
    .swiper {
      @apply flex flex-col items-center relative gap-ds-sm justify-center w-screen mt-ds-lg;

      .swiper-wrapper {
        @apply h-auto;
      }

      .swiper-slide {
        img {
          @apply w-full;
        }

        @screen md {
          /* to avoid flicker on Swiper initialisation */
          max-height: min-content;
          max-width: 518px;
        }

        .flush-image-text {
          h3 {
            @apply text-rebrandSm pt-0;
            /**
          * 1.2 is line height, 2.5rem accounts for a max of two lines of copy.
          * This ensures the copy sections of each carousel item are vertically aligned.
          **/
            min-height: calc(1.2 * 2.5rem);

            a {
              color: unset;
            }
          }

          p {
            @apply text-base;
          }
        }
      }
    }

    .swiper-navigation-container {
      @apply flex items-center mt-ds-ml mb-ds-sm mr-ds-md justify-between md:justify-center lg:justify-start z-[1];

      .swiper-button-prev,
      .swiper-button-next {
        @apply relative z-[1] min-h-ds-xl min-w-ds-xl h-auto w-auto cursor-pointer flex items-center justify-center rounded-full text-black border border-solid border-black border-opacity-20 bg-white inset-auto mt-auto;

        &:after {
          @apply text-base;
        }
      }

      .swiper-pagination.swiper-pagination-clickable.swiper-pagination-bullets.swiper-pagination-horizontal {
        @apply z-[1];
      }

      .swiper-pagination {
        @apply relative flex gap-ds-sm justify-center mx-ds-sm w-auto mt-ds-md;

        .swiper-pagination-bullet {
          @apply h-ds-md w-ds-md rounded-xl border border-solid border-black border-opacity-20 bg-opacity-10 bg-black;
        }

        .swiper-pagination-bullet-active {
          @apply opacity-100 bg-primary z-[1];
        }
      }
    }
  }
}

@media (max-width: 429px) {
  .carouselNarrow {
    @apply -ml-ds-md pl-ds-md;

    :global {
      .swiper-navigation-container {
        @apply -ml-ds-ms mr-ds-xxs;

        .swiper-button-prev,
        .swiper-button-next {
          @apply min-w-ds-lg min-h-ds-lg;
        }
      }
    }
  }
}
</style>
